package com.oasisfeng.invaluable.data

import com.oasisfeng.invaluable.Anchor
import com.oasisfeng.invaluable.Dimensions
import com.oasisfeng.invaluable.InternalTopologyApi
import com.oasisfeng.invaluable.Link
import com.oasisfeng.invaluable.Linkable
import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.NestedLinkable
import com.oasisfeng.invaluable.SchemaField
import com.oasisfeng.invaluable.rawTargetOrNull
import com.oasisfeng.invaluable.then

/**
 * Typed view over a Link whose topology is the identity of the wrapper itself.
 *
 * Implement this only when [link] is the object's own graph-native record, not a referenced target,
 * issuer, projection payload, cache key, or transport handle. Each implementation still needs
 * factory/rendering companion object as [LinkTopology] that define the exact parent/target shape;
 * this interface is only the shared boundary for wrappers that have already paid that topological cost.
 *
 * IMPLEMENTATION NOTE:
 *
 * Kotlin cannot express "every implementor must be a value class" in the interface signature. Each implementation
 * must therefore be defined as **`@JvmInline value class`** with **private constructor** over [com.oasisfeng.invaluable.NestedLink] when it is
 * only a thin view.
 * Put construction inputs in the type-specific [LinkTopology] spec, store [link] as the single value-class field,
 * and expose decoded data as context-bound derived functions rather than stored fields.
 *
 * If a type needs additional persistent state, it is not a thin [LinkView].
 */
interface LinkView : NestedLinkable { override val link: NestedLink }

/**
 * Canonical Link topology for a typed [LinkView].
 * It should be implemented by the companion object of classes implementing [LinkView].
 *
 * The template keeps construction and rendering in one path:
 *
 * - concrete topology companions expose semantic construction functions.
     * - [matchesTopology] verifies whether an existing [NestedLink] has that complete topology.
 *
 * @param ctor is supplied as the value-class constructor reference by the companion object,
 * where private constructors are still visible.
 */
abstract class LinkTopology<out T : LinkView>(protected val ctor: (NestedLink) -> T) {

    fun render(link: NestedLink): T? = link.let { if (it.matchesTopology()) ctor(it) else null }
    fun matchNode(node: NestedLink?): Boolean = node != null && render(node) != null
    protected abstract fun NestedLink.matchesTopology(): Boolean

    fun Link.matchAnchor(anchor: Anchor): Boolean = matchAnchor(anchor.link)
    fun Link.matchAnchor(anchor: NestedLink): Boolean = isChildOf(anchor)
}

/** LinkTopology for simple protocol/anchor wrapped targets: `anchor -> target`. */
abstract class AnchoredTopology<in Target, out T : LinkView>(
    ctor: (NestedLink) -> T,
    val anchor: NestedLink,
    private val targetLink: (Target) -> NestedLink,
    private val targetMatch: (NestedLink?) -> Boolean
) : LinkTopology<T>(ctor) {

    operator fun invoke(target: Target): T = ctor(anchor.then(targetLink(target)))
    override fun NestedLink.matchesTopology(): Boolean = matchAnchor(anchor) && targetMatch(nestedTargetOrNull())
}

/** LinkTopology for anchors whose target can be any visible NestedLink: `anchor -> target`. */
abstract class AnchoredLinkTopology<out T : LinkView>(ctor: (NestedLink) -> T, anchor: NestedLink)
    : AnchoredTopology<NestedLink, T>(ctor, anchor, targetLink = { it }, targetMatch = { it != null })

/** LinkTopology for anchors whose target is another typed LinkView: `anchor -> target.link`. */
abstract class AnchoredViewTopology<TargetView : LinkView, out T : LinkView>(
    ctor: (NestedLink) -> T, anchor: NestedLink, targetTopology: LinkTopology<TargetView>)
    : AnchoredTopology<TargetView, T>(ctor, anchor, targetLink = LinkView::link, targetMatch = targetTopology::matchNode)

/** Render as specified [LinkTopology]. Return null if this link does not match the complete topology. */
fun <T: LinkView> NestedLink.asOrNull(topology: LinkTopology<T>): T? = topology.render(this)

/**
 * Field-level local knowledge for a projection-indexed [SchemaTopology] item.
 *
 * This is not a partial [LinkView]. [complete] is the only typed identity view, and it is known only when the full
 * item Link resolves and renders through [topology]. The other properties expose how much of the projected item can be
 * inspected before complete materialization is possible.
 *
 * @property topology Schema contract used to decode the tuple; [fields] follows this contract's field order exactly.
 * @property projection Projection of the schema item Link itself, i.e. the relation whose parent is [parent] and target
 * is [tuple].
 * @property parent Observed non-root item parent. Domain observers interpret this as actor log, previous item, or other
 * context; schema observation only verifies that the parent preimage is visible as a [NestedLink].
 * @property tuple Observed complete physical payload chain `DATA.then(v1)...then(vn)`. This is useful for distinguishing
 * "the whole tuple chain is incomplete" from "the tuple structure is visible but a specific field value is missing".
 * @property fields Field value observations aligned with [topology]. A field may be known even when [tuple] is missing,
 * because the missing preimage can sit elsewhere in the same payload closure.
 * @property complete Fully rendered schema item, known only when parent, tuple, field domains, and topology validation
 * all succeed.
 */
data class SchemaObservation<out T : LinkView>(
    val topology: SchemaTopology<T>,
    val projection: LinkProjection,
    val parent: Observation<NestedLink>,
    val tuple: Observation<NestedLink>,
    val fields: List<ObservedSchemaField>,
    val complete: Observation<T>,
)

/**
 * One observed schema field.
 *
 * [value] is the raw field [Link] after applying the field domain validator. Domain observers should perform any
 * narrower conversion, such as `Link -> Quantity` or `Link -> NestedLink`, outside the generic schema layer.
 */
data class ObservedSchemaField(val field: SchemaField, val value: Observation<Link>)

/**
 * Observe a schema item without creating partial [LinkView] identities.
 *
 * The observer first attempts strict full rendering, then decodes the projected tuple shape enough to expose parent,
 * tuple, and field-level knowledge. Missing remote preimages remain [Observation.Missing]; malformed records or field
 * values that violate [SchemaField.domain] become [Observation.Invalid].
 */
fun <T : LinkView> LinkProjector.observeSchema(
    topology: SchemaTopology<T>,
    projection: LinkProjection,
    records: ProjectedLinkRecordResolver,
): SchemaObservation<T> {
    // One observation resolves the item, its parent, its tuple and every field — four walks over one shared DAG.
    val resolver = records.memoizing()
    val complete = resolveLink(projection, resolver).mapKnownOrInvalid("Resolved schema item does not match topology.") {
        it.nestedOrNull()?.let(topology::render)
    }
    val record = resolver.recordFor(projection)
        ?: return SchemaObservation(topology, projection, parent = Observation.Missing(projection),
            tuple = Observation.Missing(projection), fields = topology.fields.missingFields(projection), complete)
    if (record.projection != projection)
        return topology.invalidObservation(projection, "Projected schema item record returned for the wrong projection.", complete)

    val parentProjection = record.parent
        ?: return topology.invalidObservation(projection, "Schema item cannot have root parent.", complete)
    val parent = resolveLink(parentProjection, resolver)
        .mapKnownOrInvalid("Schema item parent cannot be root.", Link::nestedOrNull)

    val tupleProjection = record.target
        ?: return topology.invalidObservation(projection, "Schema item cannot target root tuple.", complete)
    val tuple = observeSchemaTuple(topology, tupleProjection, resolver)

    return SchemaObservation(topology, projection, parent, tuple.tuple, tuple.fields, complete)
}

private data class SchemaTupleObservation(
    val tuple: Observation<NestedLink>,
    val fields: List<ObservedSchemaField>,
)

private fun LinkProjector.observeSchemaTuple(
    topology: SchemaTopology<*>,
    tupleProjection: LinkProjection,
    resolver: ProjectedLinkRecordResolver,
): SchemaTupleObservation {
    val values = MutableList<Observation<Link>?>(topology.fields.size) { null }
    var currentProjection = tupleProjection

    for (index in topology.fields.indices.reversed()) {
        val record = resolver.recordFor(currentProjection)
            ?: return SchemaTupleObservation(
                Observation.Missing(currentProjection),
                topology.fields.withValues(values.fillMissingThrough(index, currentProjection)),
            )
        if (record.projection != currentProjection) return topology.invalidTuple(
            "Projected schema tuple record returned for the wrong projection.",
        )

        val targetProjection = record.target ?: return topology.invalidTuple("Schema tuple field cannot target root.")
        val field = topology.fields[index]
        values[index] = resolveLink(targetProjection, resolver).mapKnownOrInvalid(
            "Schema field '${field.name}' value does not satisfy domain validator.",
        ) { link -> link.takeIf(field.domain.validator) }
        currentProjection = record.parent ?: return topology.invalidTuple("Schema tuple data cannot have root parent.")
    }

    val tuple = when (val base = resolveLink(currentProjection, resolver)) {
        is Observation.Known -> {
            if (base.value == Dimensions.DATA()) {
                resolveLink(tupleProjection, resolver)
                    .mapKnownOrInvalid("Schema tuple cannot be root.") { it.nestedOrNull() }
            } else {
                Observation.Invalid("Schema tuple does not terminate at DATA.")
            }
        }
        is Observation.Missing -> base
        is Observation.Invalid -> base
    }
    return SchemaTupleObservation(tuple, topology.fields.withValues(values.fillMissingThrough(-1, currentProjection)))
}

private fun Array<out SchemaField>.missingFields(projection: LinkProjection): List<ObservedSchemaField> =
    map { ObservedSchemaField(it, Observation.Missing(projection)) }

private fun Array<out SchemaField>.withValues(values: List<Observation<Link>>): List<ObservedSchemaField> =
    mapIndexed { index, field -> ObservedSchemaField(field, values[index]) }

private fun MutableList<Observation<Link>?>.fillMissingThrough(
    throughIndex: Int,
    projection: LinkProjection,
): List<Observation<Link>> = mapIndexed { index, observation ->
    observation ?: Observation.Missing(projection).takeIf { index <= throughIndex }
    ?: Observation.Invalid("Schema tuple field observation was not produced.")
}

private fun SchemaTopology<*>.invalidTuple(reason: String): SchemaTupleObservation =
    SchemaTupleObservation(Observation.Invalid(reason), fields.map { ObservedSchemaField(it, Observation.Invalid(reason)) })

private fun <T : LinkView> SchemaTopology<T>.invalidObservation(projection: LinkProjection, reason: String, complete: Observation<T>)
= SchemaObservation(topology = this, projection, parent = Observation.Invalid(reason),
    tuple = Observation.Invalid(reason),
    fields = fields.map { ObservedSchemaField(it, Observation.Invalid(reason)) },
    complete = if (complete is Observation.Known) Observation.Invalid(reason) else complete,
)

/**
 * Advanced LinkTopology for Data Tuples that natively implements the V7 Dual-Chain Zipper model.
 * It enforces absolute separation between the physical layer (Schema/Data) and the semantic layer (Protocol).
 *
 * This unified class supports both:
 * 1. Stand-alone tuples: pass [schemaStructure] as the parent when constructing the tuple.
 * 2. Homogeneous list items: pass a custom parent and override [validateRoot] to define chain semantics.
 */
abstract class SchemaTopology<out T : LinkView>(
    ctor: (NestedLink) -> T,
    val protocolIdentity: NestedLink,
    vararg val fields: SchemaField
) : LinkTopology<T>(ctor) {

    /** The pure physical structure, containing no textual semantic data. */
    val schemaStructure: NestedLink = fields.fold(Dimensions.DATA()) { acc, field ->
        acc.then(field.domain.typeMarker.link)
    }

    fun matchTupleData(dataLink: NestedLink): Boolean {
        var currentData = dataLink
        // Parallel zip validation backwards
        for (i in fields.indices.reversed()) {
            // Data is too short compared to schema length
            if (currentData == Dimensions.DATA()) return false

            val domain = fields[i].domain
            @OptIn(InternalTopologyApi::class)
            val valueLink = currentData.rawTargetOrNull() ?: return false
            if (! domain.validator(valueLink)) return false

            currentData = currentData.parent().nestedOrNull() ?: return false
        }
        return currentData == Dimensions.DATA()
    }

    /** Construct a tuple item with field values in schema order. */
    protected fun constructTuple(vararg values: Linkable): T = constructTuple(schemaStructure, * values)
    protected fun constructTuple(parent: NestedLink, vararg values: Linkable): T {
        require(values.size == fields.size) {
            "Spec values size (${values.size}) does not match schema fields size (${fields.size})."
        }
        var currentData: Link = Dimensions.DATA()
        for (i in fields.indices) {
            val field = fields[i]
            val value = values[i].link
            // Build-time validation: guarantee each value conforms to its field domain validator
            require(field.domain.validator(value)) {
                "Field '${field.name}' value $value does not satisfy domain validator"
            }
            currentData = currentData.then(value)
        }
        return ctor(parent.then(currentData))
    }

    final override fun NestedLink.matchesTopology() = matchListItem(this) { validateRoot(it) }

    /**
     * Hook to validate the root context of this link item.
     * For stand-alone tuples (singleton), this defaults to verifying that the parent is the [schemaStructure] itself.
     * For homogenous list items, override this to verify that the item belongs to a valid list chain/root.
     */
    protected open fun validateRoot(link: NestedLink): Boolean = link.parent() == schemaStructure

    /**
     * Helper to match an item: validates the tuple shape and then defers to the
     * implementation's validator to verify that this item traces back to a valid root.
     */
    private fun matchListItem(link: NestedLink, rootValidator: (NestedLink) -> Boolean): Boolean {
        val target = link.target()
        val dataLink = target.nestedOrNull() ?: return false
        return matchTupleData(dataLink) && rootValidator(link)
    }
}
